import { defineLocalStore } from '@/utils/localStore'
import { computed, ref } from 'vue'
import { getDirectory } from '@/directory'
import { permissionList, type PermissionListResponse } from '@/requests/role'
import type { Permission } from '@/utils/types/role'
import {
  userAccessLevelCreate,
  userAccessLevelDelete,
  userPermissions,
  type UserPermissionsResponse,
  userUpdateRole,
} from '@/requests/user'
import { confirm } from 'best-modules/plugins'
import type { AccessLevelTypeItem, PermissionGroup } from '@/utils/types/user'

export const {
  useLocalStore: useUserCardPermissionsStore,
  initLocalStore: initUserCardPermissionsStore,
} = defineLocalStore('userCardPermissions', (core: any) => {
  const groupedPermissions = ref<Record<string, Permission[]>>({})
  const groupLabels = ref<Record<string, string>>({})
  const rolePermissions = ref<string[]>([])
  const directPermissions = ref<string[]>([])
  const deniedPermissions = ref<string[]>([])

  const permissionGroups = computed<PermissionGroup[]>(() => {
    return Object.entries(groupedPermissions.value || {}).map(
      ([groupKey, permissions]: [string, Permission[]]) => ({
        key: groupKey,
        title: groupLabels.value[groupKey] || groupKey,
        items: (permissions || []).map((permission: Permission) => {
          const name = permission.name
          const denied = deniedPermissions.value.includes(name)
          const allowed =
            !denied &&
            (directPermissions.value.includes(name) ||
              rolePermissions.value.includes(name))

          return {
            id: permission.id || name,
            label: permission.description || permission.name.split('.')[1],
            allowed,
          }
        }),
      })
    )
  })

  const accessLevelTypeIds = computed<number[]>(() => {
    if (Array.isArray(core.user.accessLevelTypeIds)) {
      return core.user.accessLevelTypeIds.map(Number)
    }

    return core.user.accessLevelTypeId
      ? [Number(core.user.accessLevelTypeId)]
      : []
  })

  const accessLevelTypes = computed<AccessLevelTypeItem[]>(() => {
    return (getDirectory('accessLevelTypes') || []) as AccessLevelTypeItem[]
  })

  const selectedAccessLevels = computed<AccessLevelTypeItem[]>(() => {
    const selectedIds = new Set(accessLevelTypeIds.value)
    const items = accessLevelTypes.value.filter(item =>
      selectedIds.has(Number(item.id))
    )

    if (
      core.user.accessLevelType &&
      selectedIds.has(Number(core.user.accessLevelType.id)) &&
      !items.some(
        item => Number(item.id) === Number(core.user.accessLevelType.id)
      )
    ) {
      items.push(core.user.accessLevelType as AccessLevelTypeItem)
    }

    return items
  })

  const availableAccessLevels = computed<AccessLevelTypeItem[]>(() => {
    const selectedIds = new Set(accessLevelTypeIds.value)

    return accessLevelTypes.value.filter(
      item => !selectedIds.has(Number(item.id))
    )
  })

  const loadPermissions = (userId?: number | string) => {
    if (!userId) {
      groupedPermissions.value = {}
      groupLabels.value = {}
      rolePermissions.value = []
      directPermissions.value = []
      deniedPermissions.value = []

      return Promise.resolve()
    }

    return Promise.all([
      permissionList(),
      userPermissions(Number(userId)),
    ]).then(
      ([permRes, userPermRes]: [
        PermissionListResponse,
        UserPermissionsResponse
      ]) => {
        groupedPermissions.value = permRes?.groups || {}
        groupLabels.value = permRes?.groupLabels || {}
        rolePermissions.value = userPermRes?.role_permissions || []
        directPermissions.value = [...(userPermRes?.direct_permissions || [])]
        deniedPermissions.value = [...(userPermRes?.denied_permissions || [])]
      }
    )
  }

  const updateUserRole = (roleId: number) => {
    if (!core.user.id || core.readonly.value) return

    return confirm({
      text: 'Ви дійсно бажаєте змінити роль доступів? Роль визначає набір прав користувача.',
    }).then(() => {
      return userUpdateRole(core.user.id, roleId)
        .then((res: UserPermissionsResponse) => {
          rolePermissions.value = res?.role_permissions || []
          directPermissions.value = [...(res?.direct_permissions || [])]
          deniedPermissions.value = [...(res?.denied_permissions || [])]

          core.getUser()
        })
        .catch(() => core.getUser())
    })
  }

  const addAccessLevel = (accessLevel: AccessLevelTypeItem) => {
    if (!core.user.id || core.readonly.value) return

    return userAccessLevelCreate(core.user.id, accessLevel.id).then(res => {
      if (res) {
        core.setUser(res)
        return
      }

      core.user.accessLevelTypeIds = [
        ...accessLevelTypeIds.value,
        Number(accessLevel.id),
      ]
    })
  }

  const removeAccessLevel = (accessLevel: AccessLevelTypeItem) => {
    if (!core.user.id || core.readonly.value) return

    return userAccessLevelDelete(core.user.id, accessLevel.id).then(res => {
      if (res) {
        core.setUser(res)
        return
      }

      core.user.accessLevelTypeIds = accessLevelTypeIds.value.filter(
        id => id !== Number(accessLevel.id)
      )
    })
  }

  return {
    addAccessLevel,
    availableAccessLevels,
    loadPermissions,
    permissionGroups,
    removeAccessLevel,
    selectedAccessLevels,
    updateUserRole,
  }
})
